<script lang="ts" setup>
import TextEditor from '@/components/editor/TextEditor.vue'
import LinkEmbed from '@/components/editor/embed-editors/LinkEmbed.vue'
import { Embed } from '@/components/editor/extensions/embed'
import { Link } from '@/components/editor/extensions/link'
import Embeds from '@/components/editor/settings/Embeds.vue'
import { GroupState } from '@/composition/groups'
import { PostState } from '@/composition/posts'
import { ExtendedImage, useEditor } from '@/composition/useEditor'
import { IPosts } from '@/models/Posts'
import { useGlobalStore } from '@/store/app'
import { MAX_POST_ATTACHMENTS_LIMIT } from '@/util/const'
import {
  ChevronDownIcon,
  PlusIcon,
  XCloseIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UICard,
  UIModal,
  UIModalHeader,
  UISelect,
  UISwitch,
  UITag,
  UITextLgMedium,
  UITextMdMedium,
  UITextSmMedium,
  UITextSmNormal,
  useNotification,
} from '@gohighlevel/ghl-ui'
import Placeholder from '@tiptap/extension-placeholder'
import StarterKit from '@tiptap/starter-kit'
import { Editor, BubbleMenu as bubblemenu } from '@tiptap/vue-3'
import { v4 as uuidv4 } from 'uuid'
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  useRoute,
  useRouter,
} from 'vue-router'
//import EmojiPicker from '@/components/editor/embed-editors/EmojiPicker.vue'
import { OtherMentions } from '@/components/editor/extensions/other-mention'
import { UserMention } from '@/components/editor/extensions/user-mention'
import { app } from '@/composition/app'
import { UserState } from '@/composition/users'
import { HeadingConfiguration } from '@/helper/Editor.helper'
import {
  checkNativeKeyboardResize,
  handleEditorVisibility,
} from '@/util/helper'
import { Capacitor, registerPlugin } from '@capacitor/core'
import BubbleMenu from '@tiptap/extension-bubble-menu'
import HardBreak from '@tiptap/extension-hard-break'
import { Plugin } from 'prosemirror-state'
import { useI18n } from 'vue-i18n'
import { emitter } from '../../main'
import BadgedAvatar from '../common/avatar/BadgedAvatar.vue'
import CommunityButton from '../common/buttons/CommunityButton.vue'
import EmojiLoader from '../editor/embed-editors/EmojiLoader.vue'
import { CustomTextStyle } from '../editor/extensions/CustomTextStyle'
import {
  addEventsToMentions,
  getUniqueMentions,
  trackMentions,
} from '../mention-helper'

const ChannelListPostEditor = defineAsyncComponent(
  () => import('@/components/channels/mobile/ChannelListPostEditor.vue')
)
const PostAttachments = defineAsyncComponent(
  () => import('./PostAttachments.vue')
)
const AttachmentsPreview = defineAsyncComponent(
  () => import('@/components/common/AttachmentsPreview.vue')
)
const MediaEmbed = defineAsyncComponent(
  () => import('@/components/editor/embed-editors/MediaEmbed.vue')
)
const GifPicker = defineAsyncComponent(
  () => import('@/components/editor/embed-editors/GifPicker.vue')
)
const ChannelSettingsIndex = defineAsyncComponent(
  () => import('@/components/channels/ChannelSettingsIndex.vue')
)
const UIConfirmation = defineAsyncComponent(
  () => import('@/components/common/modal/UIConfirmation.vue')
)
const EmojiPicker = defineAsyncComponent({
  loader: async () => {
    isLoading.value = true
    const component = await import(
      '@/components/editor/embed-editors/EmojiPicker.vue'
    )
    isLoading.value = false
    return component
  },
  loadingComponent: EmojiLoader,
  delay: 200,
})

const props = defineProps({
  post: Object,
  showEditor: Boolean,
  autoFocus: Boolean,
})

const emit = defineEmits([
  'update:show',
  'update:save',
  'update:edited',
  'add:embed',
])
const showEditor = ref(props.showEditor)
const isLoading = ref(false)
const unSavedChanges = ref(false)
const postContent = props.post?.content ? ref(props.post?.content) : ref('')
const selectedChannelId = ref()
const isCommentDisabled = ref(props.post?.isCommentDisabled || false)
const { t } = useI18n()
const notification = useNotification()

function showNotification(title: string, content: string) {
  notification.create({
    type: 'warning',
    title: title,
    content: content,
    duration: 3000,
  })
}
// need to add this here
watch(
  () => selectedChannelId.value,
  (newValue, _) => {
    const channel = GroupState.state.channelList.find(
      (channel: any) => channel._id === newValue
    )
    GroupState.state.editorCurrentChannel = channel
  }
)

const router = useRouter()
const isChannelSelectable = ref(true)
const channels: any = ref([])
const postContentRef = ref<HTMLElement | null>(null)
const route = useRoute()
const isUploadingPost = ref(false)
const selectedEmbed = ref('')
const attachments = props.post?.attachments
  ? ref<any>(props.post?.attachments)
  : ref<any>([])
const postTitle = props.post?.title ? ref(props.post.title) : ref('')
const uploadingAttachments = ref(false)
const chanelMissingAlert = ref(false)
const showPostPreview = ref(false)
const currentPostIndex = ref(0)
const showConfirmationModal = ref(false)
const showChannelModal = ref(false)
const showChannelDrawer = ref(false)
const ContentUploadPlugin: any = Capacitor.isNativePlatform()
  ? registerPlugin('ContentUpload')
  : null
const KeyboardEventsPlugin: any = registerPlugin('KeyboardEvents')

const nextRoute = reactive({
  fn: '' as any,
})
const menu = ref<HTMLElement | null>()

const ExtendedImageWithPaste = ExtendedImage.extend({
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            paste(_, event) {
              if (!event?.clipboardData?.files?.length) {
                return
              }
              const files = Array.from(event.clipboardData.files).filter(file =>
                /^(video\/|image\/)/.test(file.type?.toLowerCase())
              )
              if (!files || !files.length) {
                return
              }

              const modifiedFiles: any = []

              for (let i = 0; i < files.length; i++) {
                const randomString = Math.random().toString().slice(2, 10)
                const originalName = files[i].name.replace(/#/g, '')
                const newName = `comm-media__${randomString}_${originalName}`

                const modifiedFile: any = new File([files[i]], newName, {
                  type: files[i].type,
                  lastModified: files[i].lastModified,
                })

                modifiedFile.status = 'not-started'
                modifiedFile.id = uuidv4()
                modifiedFiles.push(modifiedFile)
              }
              onEmbedSelect(modifiedFiles)
            },
          },
        },
      }),
    ]
  },
})

const editor = new Editor({
  onFocus() {
    emitter.emit('embed:show-toolbar')
  },
  // content: postContent.value,
  extensions: [
    StarterKit.configure({
      heading: false,
    }),
    HeadingConfiguration,
    HardBreak.extend({
      addKeyboardShortcuts() {
        return {
          Enter: () => {
            this.editor.commands.setHardBreak()
            return true
          },
        }
      },
    }),
    ExtendedImageWithPaste,
    CustomTextStyle,
    Placeholder.configure({
      placeholder: t('createPost.placeholder'),
    }),
    Embed,
    Link.configure({
      openOnClick: false,
      protocols: ['mailto', 'tel'],
    }),
    UserMention,
    OtherMentions,
    BubbleMenu.configure({
      element: menu.value,
    }),
  ],
  onUpdate({ editor }) {
    unSavedChanges.value = true
    const htmlContent = editor.getHTML()
    // Remove leading empty <p> tags
    let cleanedContent = htmlContent.replace(/^(<p>\s*<\/p>)+/, '')
    // Remove trailing empty <p> tags
    cleanedContent = cleanedContent.replace(/(<p>\s*<\/p>)+$/, '')
    postContent.value = cleanedContent
    setTimeout(() => {
      addEventsToMentions(editor.view.dom, null, router)
    }, 100)
  },
  parseOptions: {
    preserveWhitespace: 'full',
  },
})
const { linkValues, onLinkAdd, onEmojiAdd, onGifAdd, onHashtagAdd } =
  useEditor(editor)
const embedsComponentMapper = {
  image: {
    component: MediaEmbed,
    meta: {
      type: 'image',
      capture: true,
      allowedTypes: t('embeds.allowedTypes'),
      header: t('embeds.uploadImage'),
      isCommentEditor: app.state.isMobile,
    },
  },
  video: {
    component: MediaEmbed,
    meta: {
      placeholder: t('embeds.uploadVideoPlaceholder'),
      type: 'video',
      allowedTypes: t('embeds.uploadVideoAllowedTypes'),
      header: t('embeds.uploadVideo'),
      isCommentEditor: app.state.isMobile,
    },
  },
  attachment: {
    component: MediaEmbed,
    meta: {
      type: 'attachment',
      allowedTypes: t('embeds.uploadAttachment'),
      header: t('embeds.addAttachment'),
      isCommentEditor: app.state.isMobile,
    },
  },
  emoji: {
    component: EmojiPicker,
    meta: {
      type: 'emoji',
    },
  },
  gif: {
    component: GifPicker,
    meta: {
      type: 'gif',
    },
  },
}

onBeforeUnmount(() => {
  handleEditorVisibility(false)
  if ('virtualKeyboard' in navigator) {
    // @ts-ignore
    navigator.virtualKeyboard.removeEventListener(
      'geometrychange',
      handleGeometryChange
    )
  }

  window.visualViewport?.removeEventListener(
    'resize',
    handleVisualViewportResize
  )

  window.removeEventListener('focus', handleInputFocusOnResize, true)
  window.removeEventListener('click', handleContentEditorFocus)
  GroupState.state.editorCurrentChannel = null

  // Cancel any ongoing uploads
  if (Capacitor.isPluginAvailable('ContentUpload')) {
    ContentUploadPlugin.cancelAllUploads()
  }
})

function handleInputFocusOnResize(focusEvent: any) {
  if (!props.showEditor || !showEditor.value) {
    return
  }

  nextTick(() => {
    window.scrollTo({
      behavior: 'smooth',
      left: 0,
      top: 2,
    })
  })

  focusEvent.preventDefault()
}

onMounted(async () => {
  const hasNativeKeyboardResize = await checkNativeKeyboardResize(
    KeyboardEventsPlugin
  )
  if ('virtualKeyboard' in navigator) {
    // @ts-ignore
    navigator.virtualKeyboard.overlaysContent = true
    // @ts-ignore
    navigator.virtualKeyboard.addEventListener(
      'geometrychange',
      handleGeometryChange
    )
  } else if (!hasNativeKeyboardResize) {
    //Add event listener to listen to visual viewport changes
    window.visualViewport?.addEventListener(
      'resize',
      handleVisualViewportResize
    )

    // Handle the scroll which safari makes, when input is highlighted
    window.addEventListener('focus', handleInputFocusOnResize, true)
    window.addEventListener('click', handleContentEditorFocus)
  }

  embedsComponentMapper['link'] = {
    component: LinkEmbed,
    meta: {
      placeholder: 'Link',
      initialValues: linkValues,
      type: 'link',
    },
  }
  channels.value = GroupState.state.nonRestrictedChannelList.map(
    (channel: any) => {
      if (
        route.params?.channelSlug &&
        channel.slug === route.params.channelSlug
      ) {
        selectedChannelId.value = channel._id
        isChannelSelectable.value = false
      }
      return {
        label: channel.name,
        value: channel._id,
      }
    }
  )
  if (props.post) {
    selectedChannelId.value = props.post.channelId
    isChannelSelectable.value = false

    if (postContent.value) {
      // Sanitize the content before setting it to the editor
      editor.commands.setContent(postContent.value, true) // true is for trigger the onUpdate event
    }
  }
  if (!selectedChannelId.value && channels.value.length) {
    const channel: { label: string; value: string } = channels.value[0]
    selectedChannelId.value = channel?.value
    GroupState.state.editorCurrentChannel = GroupState.state.channelList.find(
      (channel: any) => channel._id === selectedChannelId.value
    )
  }

  // When mounted and the component is ready, make the unsavedChanges false, since user has not made any changes yet.
  unSavedChanges.value = false
})

emitter.on('useraction:media-upload', () => {
  if (app.state.isMobile) {
    handlePostEditorHeight()
  }
})

onBeforeRouteUpdate((to, from, next) => {
  checkForUnsavedChanges(to, from, next)
})

onBeforeRouteLeave((to, from, next) => {
  checkForUnsavedChanges(to, from, next)
})

function onEmbedSelect(data: any) {
  unSavedChanges.value = true
  if (selectedEmbed.value === 'link') {
    onLinkAdd(data)
  } else if (selectedEmbed.value === 'emoji') {
    onEmojiAdd(data)
  } else if (selectedEmbed.value === 'gif') {
    onGifAdd(data.webp, data.width, data.height, '480px', 'post')
  } else if (selectedEmbed.value === 'hashtag') {
    onHashtagAdd()
    selectedEmbed.value = '' // Close the embed selection after inserting hashtag
  } else {
    if (attachments.value?.length + data.length > MAX_POST_ATTACHMENTS_LIMIT) {
      showNotification(
        t('embeds.limitExceeded'),
        t('embeds.limitExceededMessage')
      )
      return
    }
    attachments.value.push(...data)
    uploadingAttachments.value = true
  }
}

function attachEmbedLink(file: any) {
  unSavedChanges.value = true
  if (attachments.value?.length >= MAX_POST_ATTACHMENTS_LIMIT) {
    showNotification(
      t('embeds.limitExceeded'),
      t('embeds.limitExceededMessage')
    )
    return
  }
  attachments.value.push(file)
}

function cancelCallback() {
  handleEditorVisibility(false)
  document.body.style.position = ''
  if (unSavedChanges.value) {
    showConfirmationModal.value = true
    return false
  }
  selectedEmbed.value = ''
  emit('update:show', false)
  showEditor.value = false
}

function changeEmbed(value: any) {
  // Handle hashtag directly without setting selectedEmbed
  if (value === 'hashtag') {
    unSavedChanges.value = true
    onHashtagAdd()
    return
  }

  if (app.state.isMobile) {
    // Allow selected embed, in case of mobile since drag and drop feature is unavailable
    selectedEmbed.value = '' // Reset the previoius selectedEmbed
    nextTick(() => {
      selectedEmbed.value = value
    })
    return
  }
  if (selectedEmbed.value === value) {
    selectedEmbed.value = ''
    return
  }
  selectedEmbed.value = value
}

function changeFile(file: any, index: number) {
  attachments.value[index] = file
  checkFilesUploaded()
}

function startTyping() {
  //editor.view.focus()
}

function removeFile(index: number) {
  attachments.value.splice(index, 1)
  checkFilesUploaded()
}

const isEditorDisabled = computed(() => {
  const contentData = document.createElement('div')
  contentData.innerHTML = postContent.value.replace(/><(?=[^/])/g, '>\n<')
  let textContent = contentData.innerText || contentData.textContent || ''

  // Clean the text content by removing extra whitespace, newlines, and other unwanted characters
  textContent = textContent
    .replace(/[\s\n\r\t]+/g, '') // collapse all whitespace, newlines, tabs, carriage returns into a single space
    .trim()
  contentData.remove()

  // Check if any non-embedded attachment is still uploading (status !== 'completed')
  const isAnyAttachmentUploading = attachments.value.some(
    (attachment: any) =>
      // Only check upload status for non-embedded attachments
      !attachment.provider_name && attachment.status !== 'completed'
  )

  return (
    !postTitle.value?.length || !textContent.length || isAnyAttachmentUploading
  )
})

async function closeAlert() {
  chanelMissingAlert.value = false
}

const selectedChannelName = computed(() => {
  // return 'testing'
  const channel = channels.value.filter(
    channel => channel.value == selectedChannelId.value
  )[0]
  const tempChannel = Object.assign({}, channel)
  return tempChannel
})
async function savePost() {
  try {
    handleEditorVisibility(false)
    if (selectedChannelId.value === undefined) {
      chanelMissingAlert.value = true
      return
    } else {
      isUploadingPost.value = true
      const { locationId, groupId, userId } = useGlobalStore().$state
      const parser = new DOMParser()
      const content = parser.parseFromString(postContent.value, 'text/html')

      const broadcastElements = content.querySelectorAll(
        'span[data-type="mention"][data-mention-type="broadcast"]'
      )
      let notifyAllGroupMembers = false
      if (broadcastElements.length > 0) {
        // notify all group memebrs
        notifyAllGroupMembers = true
      }
      const uniqueMentions = getUniqueMentions(content)
      trackMentions('post', uniqueMentions)

      const data: IPosts = {
        locationId: locationId,
        groupId: groupId as string,
        channelId: selectedChannelId.value,
        title: postTitle.value,
        content: postContent.value,
        mentions: uniqueMentions.user,
        channelMentions: uniqueMentions.channel,
        eventMentions: uniqueMentions.event,
        courseMentions: uniqueMentions.course,
        attachments: attachments.value,
        createdBy: userId,
        notifyAllGroupMembers: notifyAllGroupMembers,
        isCommentDisabled: isCommentDisabled.value ?? false,
      }
      const posts = await PostState.createPost(data)
      isUploadingPost.value = false
      //TODO - change dropdown to select and remove stub channelId
      //TODO - get user data from store
      emit('update:save', posts)
      handleCancel()
    }
  } catch (error: any) {
    if (error?.response?.data?.errors?.content?.kind === 'maxContentByteSize') {
      notification.error({
        title: 'Error creating post',
        content: error?.response?.data?.errors?.content?.message,
        duration: 5000,
      })
    } else {
      notification.error({
        title: 'Error creating post',
        content: 'Something went wrong, please try again later.',
        duration: 5000,
      })
      console.error(error)
    }
  }
}

async function updatePost() {
  try {
    handleEditorVisibility(false)
    if (selectedChannelId.value === undefined) {
      chanelMissingAlert.value = true
      return
    } else {
      isUploadingPost.value = true

      const parser = new DOMParser()
      const content = parser.parseFromString(postContent.value, 'text/html')
      const broadcastElements = content.querySelectorAll(
        'span[data-type="mention"][data-mention-type="broadcast"]'
      )
      let notifyAllGroupMembers = false
      if (broadcastElements.length > 0) {
        // notify all group memebrs
        notifyAllGroupMembers = true
      }

      const uniqueMentions = getUniqueMentions(content)

      const data: IPosts = {
        _id: props.post?._id,
        locationId: props.post?.locationId,
        groupId: props.post?.groupId as string,
        channelId: props.post?.channelId,
        title: postTitle.value,
        content: postContent.value,
        mentions: uniqueMentions.user,
        channelMentions: uniqueMentions.channel,
        eventMentions: uniqueMentions.event,
        attachments: attachments.value,
        createdBy: props.post?.createdBy,
        notifyAllGroupMembers: notifyAllGroupMembers,
        isCommentDisabled: isCommentDisabled.value ?? false,
      }

      const post = await PostState.updatePost(data, true)
      isUploadingPost.value = false

      emit('update:edited', post)
      emit('update:show')
      handleCancel()
    }
  } catch (error: any) {
    if (error?.response?.data?.errors?.content?.kind === 'maxContentByteSize') {
      notification.error({
        title: 'Error updating post',
        content: error?.response?.data?.errors?.content?.message,
        duration: 5000,
      })
    } else {
      notification.error({
        title: 'Error updating post',
        content: 'Something went wrong, please try again later.',
        duration: 5000,
      })
      console.error(error)
    }
  }
}

function handleCancel() {
  // make unsaved changes false, since the user has either saved or updated the changes
  unSavedChanges.value = false
  showEditor.value = false
  postTitle.value = ''
  postContent.value = ''
  attachments.value = []
  uploadingAttachments.value = false
}

function updatePreview(index: number) {
  currentPostIndex.value = index
  showPostPreview.value = true
}

function checkForUnsavedChanges(_to: any, _from: any, next: any) {
  if (unSavedChanges.value) {
    showConfirmationModal.value = true
    nextRoute.fn = next
  } else {
    next()
  }
}

function goBack() {
  showConfirmationModal.value = false
  unSavedChanges.value = false
  try {
    nextRoute.fn()
  } catch {
    // If the modal is not triggered via route change, close the modal
    cancelCallback()
  }
}

function addChannel() {
  showChannelModal.value = true
}
function closeAddChannel() {
  showChannelModal.value = false
}

function checkFilesUploaded() {
  attachments.value.forEach((item: any) => {
    if (item.status !== 'completed') {
      uploadingAttachments.value = true
      return
    }
  })
  uploadingAttachments.value = false
}

const authorRole = computed(() => {
  return GroupState.state.groupAdmins.get(
    UserState.state.currentUser?.contactId
  )?.role
})

const editingText = computed(() => {
  return props.post ? t('createPost.editingIn') : t('createPost.postingIn')
})

function handleMobileChannelDrawerSelect(value) {
  if (value == 'add-channel') {
    showChannelModal.value = true
  } else selectedChannelId.value = value
  showChannelDrawer.value = !showChannelDrawer.value
}
function handleChannelDrawerTriggerMobile() {
  if (isChannelSelectable.value) {
    showChannelDrawer.value = true
  }
}
function handleEditorAddLink() {
  if (editor.isActive('link')) {
    editor?.chain().focus().unsetLink().run()
  } else {
    editor.chain().blur()
    const { view, state } = editor
    const { from, to } = view.state.selection
    const text = state.doc.textBetween(from, to, '')
    embedsComponentMapper['link'].meta.initialValues = {
      href: '',
      text,
    } as any
    changeEmbed('link')
  }
}

function handlePostEditorHeight() {
  setTimeout(() => {
    const postEditorModal = document.querySelector(
      '.post-editor-modal'
    ) as HTMLElement
    if (postEditorModal) {
      postEditorModal.style.maxHeight = `100vh`
      postEditorModal.style.removeProperty('background-color')
    }
  }, 100)
}

function handleGeometryChange(changeEvent) {
  const { height } = changeEvent.target.boundingRect
  if (app.state.isMobile) {
    if (height > 0) {
      // Keyboard is open with height, so adjust the modal height such that it is not hidden behind the keyboard + 75px for the toolbar
      const postEditorModal = document.querySelector(
        '.post-editor-modal'
      ) as HTMLElement
      if (postEditorModal) {
        setTimeout(() => {
          postEditorModal.style.maxHeight = `${
            window.innerHeight - height - 75
          }px`
        }, 100)
        // select the current .n-modal-mask and make the background white
        const modalMaskNodelist = document.querySelectorAll('.n-modal-mask')
        const modalMask = modalMaskNodelist[
          modalMaskNodelist.length - 1
        ] as HTMLElement
        modalMask.setAttribute('style', 'background-color:white !important')
      }
    } else {
      handlePostEditorHeight()
    }
  }
}

function handleContentEditorFocus(clickEvent: any) {
  if (clickEvent.target.className === 'n-card__content') {
    nextTick(() => {
      //editor.view.dom.focus()
    })
  }
}

function handleVisualViewportResize() {
  // Keyboard likely is open.
  if (app.state.isMobile) {
    if (
      window.visualViewport &&
      window.visualViewport?.height < window.innerHeight
    ) {
      const postEditorModal = document.querySelector(
        '.post-editor-modal'
      ) as HTMLElement

      if (postEditorModal && window.visualViewport) {
        postEditorModal.style.maxHeight = `${
          window.visualViewport.height - 75
        }px`
      }

      const toolbar = document.getElementById('interaction-toolbar')
      if (toolbar && window.visualViewport) {
        // Toolbar height = Keyboard height + 75px
        toolbar.style.height = `${
          window.innerHeight - window.visualViewport.height + 75
        }px`
      }

      const modalMask = document.querySelector('.n-modal-mask') as HTMLElement
      modalMask.setAttribute('style', 'background-color:white !important')
      document.body.style.position = 'fixed'
    } else if (window.visualViewport?.height == window.innerHeight) {
      const postEditorModal = document.querySelector(
        '.post-editor-modal'
      ) as HTMLElement

      if (postEditorModal && window.visualViewport) {
        postEditorModal.style.maxHeight = `${window.visualViewport.height}px`
        postEditorModal.style.removeProperty('background-color')
      }

      const toolbar = document.getElementById('interaction-toolbar')
      if (toolbar && window.visualViewport) {
        toolbar.style.height = '75px'
      }
      document.body.style.position = ''
    }
  }
}

watch(
  () => showEditor.value,
  () => {
    handleEditorVisibility(showEditor.value)
  },
  { immediate: true }
)
</script>

<template>
  <UIModal
    id="drawer-target"
    :show="showEditor"
    :auto-focus="false"
    :z-index="2001"
    class-name="post-editor-modal flex h-full w-full rounded-none md:h-auto md:rounded-xl"
    @update:show="cancelCallback"
  >
    <template #header>
      <UIModalHeader id="post-editor-header" type="custom">
        <template #customHeader>
          <div
            v-if="app.state.isMobile"
            class="fixed left-0 right-0 top-0 z-50 h-[50px] w-full bg-communities-background"
          >
            <UICard
              class="h-full w-full !rounded-none bg-communities-background"
            >
              <div
                class="inline-flex h-full w-full items-center justify-between bg-communities-background"
              >
                <div
                  class="flex items-start justify-center gap-[13px] bg-communities-background shadow-2xl"
                >
                  <div class="flex items-start justify-start gap-1">
                    <div class="inline-flex flex-col items-start justify-start">
                      <div class="flex flex-row items-center justify-center">
                        <div class="relative h-6 w-6 cursor-pointer">
                          <XCloseIcon @click="cancelCallback" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="post">
                  <UITextLgMedium class="ml-3 text-communities-font-primary">
                    {{ t('post.editPost') }}
                  </UITextLgMedium>
                </div>
                <div v-else>
                  <UITextLgMedium class="ml-3 text-communities-font-primary">
                    {{ t('post.createPost') }}
                  </UITextLgMedium>
                </div>
                <div class="flex items-center justify-end">
                  <div class="flex w-12 items-start justify-start gap-4">
                    <div
                      class="relative flex h-10 w-10 cursor-pointer flex-col items-center justify-center"
                    >
                      <UIButton
                        v-if="post"
                        id="post-edit__btn"
                        :text="true"
                        class="flex rounded-lg"
                        :loading="isUploadingPost"
                        :disabled="isEditorDisabled"
                        @click="updatePost"
                        ><UITextMdMedium>{{
                          t('post.update')
                        }}</UITextMdMedium></UIButton
                      >
                      <UIButton
                        v-else
                        id="post-edit__btn"
                        :text="true"
                        class="flex rounded-lg"
                        :loading="isUploadingPost"
                        :disabled="isEditorDisabled"
                        @click="savePost"
                        ><UITextMdMedium>{{
                          t('post.publish')
                        }}</UITextMdMedium></UIButton
                      >
                    </div>
                  </div>
                </div>
              </div>
            </UICard>
          </div>
          <div
            class="flex flex-row items-start pb-2"
            :class="app.state.isMobile ? 'pt-[60px]' : ''"
          >
            <BadgedAvatar
              :avatar-url="UserState.state.currentUser?.avatar"
              :full-name="UserState.state.currentUser?.fullName"
              :show-badge="false"
              :role="authorRole"
              :size="40"
              :level="
                UserState.state.currentUser?.gamificationMeta?.level?.toString()
              "
            />
            <UITextLgMedium
              v-if="!app.state.isMobile"
              class="ml-3.5 font-semibold text-communities-font-primary"
            >
              <div class="inline-block max-w-full align-top">
                <div
                  class="inline-block max-w-[16rem] overflow-hidden text-ellipsis whitespace-nowrap align-middle sm:max-w-full"
                >
                  {{
                    (UserState.state.currentUser?.fullName ||
                      t('userProfilePopover.deletedUser')) + '&nbsp;'
                  }}
                </div>
                <div
                  class="line-clamp-1 inline-block truncate whitespace-pre-wrap align-middle"
                >
                  <span class="font-normal text-communities-font-secondary">{{
                    editingText
                  }}</span>
                  {{ GroupState.state.groupData.name }}
                </div>
              </div>
            </UITextLgMedium>
            <UITextSmMedium
              v-else
              class="ml-3 grow font-semibold text-communities-font-primary"
            >
              <div class="inline-block max-w-full align-top">
                <div
                  class="text-black-700 inline-block max-w-[16rem] overflow-hidden text-ellipsis whitespace-nowrap align-middle text-lg leading-6 sm:max-w-full"
                >
                  {{
                    (UserState.state.currentUser?.fullName ||
                      t('userProfilePopover.deletedUser')) + '&nbsp;'
                  }}
                </div>
                <div
                  class="line-clamp-1 inline-block flex flex-row items-center justify-start align-middle"
                >
                  <span class="font-normal text-communities-font-secondary">{{
                    editingText
                  }}</span>
                  <div
                    id="channel_select_mobile"
                    class="mx-1 flex items-center justify-start rounded-md bg-gray-100 px-1.5 py-1"
                    :class="isChannelSelectable && 'cursor-pointer'"
                    tabindex="0"
                    role="listbox"
                    aria-label="Channel Select"
                    @click="handleChannelDrawerTriggerMobile"
                    @keyup.enter="handleChannelDrawerTriggerMobile"
                  >
                    <div
                      class="flex flex-row text-center text-sm font-medium leading-tight text-zinc-700"
                    >
                      <span class="flex flex-row items-center justify-center"
                        >{{ selectedChannelName.label }}
                        <ChevronDownIcon
                          v-if="isChannelSelectable"
                          class="h-4 w-4"
                      /></span>
                    </div>
                  </div>
                </div>
              </div>
            </UITextSmMedium>
          </div>
        </template>
      </UIModalHeader>
    </template>
    <div
      class="select-none px-[2px]"
      :class="{
        'post-editor-content-scroll': !app.state.isMobile,
      }"
      :style="app.state.isMobile ? `margin-top: 4px` : `margin-top: 24px`"
    >
      <div id="post-editor-mobile-input">
        <!-- DO NOT RENAME THE id of below input -->
        <input
          id="post-editor-input-title-element"
          v-model="postTitle"
          :tabindex="-1"
          type="text"
          class="rounded-md border-communities-border bg-communities-background pr-2 text-base text-communities-font-primary placeholder:text-left placeholder:text-base placeholder:text-communities-font-secondary"
          :class="
            app.state.isMobile
              ? '!border-0 py-0 pl-0 !text-xl font-normal placeholder:text-gray-500'
              : 'py-2 pl-2'
          "
          :placeholder="
            app.state.isMobile ? t('common.title') : t('post.whatIsYourTitle')
          "
          @focus="() => emitter.emit('embed:show-toolbar')"
          @change="unSavedChanges = true"
        />
        <TextEditor
          id="post-editor-description-area"
          ref="postContentRef"
          :editor="editor"
          :class="app.state.isMobile ? '!border-0' : ''"
        />
        <div v-if="app.state.isMobile" id="scroll-to-end-container" />
        <bubblemenu
          v-if="editor && !editor.isActive('image')"
          :editor="editor"
          :should-show="({ from, to }) => (to - from > 2 ? true : false)"
          :tippy-options="{
            placement: 'auto',
            offset: [0, 10],
            popperOptions: {
              modifiers: [
                {
                  name: 'flip',
                  options: {
                    fallbackPlacements: ['top'],
                    padding: { top: 20, bottom: 100 },
                  },
                },
              ],
            },
          }"
        >
          <div>
            <CommunityButton
              id="add_link__btn"
              :button-type="'primary'"
              class="cursor-pointer rounded-md px-2"
              size="small"
              @click="handleEditorAddLink"
              >{{
                editor.isActive('link')
                  ? t('post.removeLink')
                  : t('post.addLink')
              }}</CommunityButton
            >
          </div>
        </bubblemenu>
      </div>
      <div v-if="selectedEmbed" id="embeds-editor" class="mb-2">
        <component
          :is="embedsComponentMapper[selectedEmbed].component"
          :meta="embedsComponentMapper[selectedEmbed].meta"
          :render-grid-items-number="5"
          @add:embed="onEmbedSelect"
          @add:embed-link="attachEmbedLink"
          @update:close-embed="changeEmbed('')"
        />
      </div>
      <div class="post-attachments flex overflow-x-scroll py-3">
        <div v-for="(file, index) in attachments" :key="file.id">
          <PostAttachments
            :file="file"
            :index="index"
            :is-editor="true"
            class="mr-4"
            @update:show="updatePreview"
            @file:uploaded="changeFile"
            @update:remove="removeFile"
          />
        </div>
      </div>
      <AttachmentsPreview
        :show-modal="showPostPreview"
        :file="attachments[currentPostIndex]"
        :current-index="currentPostIndex"
        :total-attachments="attachments?.length"
        @update:close="showPostPreview = false"
        @update:show="updatePreview"
      />
      <UIConfirmation
        :height="250"
        :show="showConfirmationModal"
        :title="t('post.unsavedChanges')"
        :message="t('post.unsavedChangesMessage')"
        type="warning"
        :button-type="'primary'"
        @update:show="showConfirmationModal = $event"
        @update:confirmed="goBack"
      />
    </div>
    <ChannelSettingsIndex
      v-if="showChannelModal"
      type="create"
      :show="showChannelModal"
      @update:show="showChannelModal = $event"
      @update:confirmed="closeAddChannel"
    />
    <template #footer>
      <div v-if="!app.state.isMobile">
        <div class="flex items-center gap-2 pb-2 pt-8">
          <UISwitch
            id="comments-disabled-switch"
            v-model:value="isCommentDisabled"
            :unchecked-value="false"
            :checked-value="true"
          />
          <UITextSmNormal class="text-communities-font-primary">
            {{ t('post.turnOffCommenting') }}
          </UITextSmNormal>
        </div>
        <div class="post-embeds pt-4">
          <div
            class="flex flex-row items-center justify-between gap-4 text-communities-font-secondary sm:items-center md:items-center"
          >
            <Embeds
              v-if="!app.state.isMobile"
              :editor="editor"
              :selected-embed="selectedEmbed"
              :is-wide-screen="!app.state.isMobile"
              editor-type="PostEditor"
              @update:selectedEmbed="changeEmbed"
            />
            <div
              class="flex w-full flex-row items-center sm:mt-0 sm:justify-end"
            >
              <UITag
                v-if="chanelMissingAlert"
                id="errorAlert"
                type="error"
                :closable="true"
                class="pl-2"
                @close="closeAlert"
              >
                {{ t('post.selectChannel') }}
              </UITag>
              <UISelect
                v-if="!showChannelModal && !app.state.isMobile"
                id="channel_select"
                v-model:value="selectedChannelId"
                :options="channels"
                :filterable="true"
                placeholder="Select Channel"
                :disabled="!isChannelSelectable"
                class="mb-[2px] w-full sm:w-2/3"
              >
                <template #action>
                  <div
                    v-if="GroupState.state.isGroupAdmin"
                    class="flex items-center justify-center py-2 text-communities-font-secondary"
                  >
                    <UIButton
                      id="add-channel--button"
                      :text="true"
                      @click="addChannel"
                    >
                      <PlusIcon class="h-5 w-5" /> {{ t('post.addChannel') }}
                    </UIButton>
                  </div>
                </template>
              </UISelect>
            </div>
          </div>
        </div>
        <div class="mt-4 flex items-center justify-between">
          <CommunityButton
            id="post-cancel__btn"
            class="!h-12 w-auto uppercase"
            button-type="tertiary"
            @click="cancelCallback"
          >
            {{ t('common.cancel') }}
          </CommunityButton>
          <CommunityButton
            v-if="post"
            id="post-publish__btn"
            button-type="primary"
            class="!h-12 w-auto uppercase"
            :loading="isUploadingPost"
            :disabled="isEditorDisabled"
            @click="updatePost"
          >
            {{ t('post.update') }}
          </CommunityButton>
          <CommunityButton
            v-else
            id="post-publish__btn"
            :button-type="'primary'"
            class="!h-12 w-auto uppercase"
            :loading="isUploadingPost"
            :disabled="isEditorDisabled"
            @click="savePost"
          >
            {{ t('post.publishPost') }}
          </CommunityButton>
        </div>
      </div>
    </template>
  </UIModal>
  <ChannelListPostEditor
    v-if="app.state.isMobile"
    :show="showChannelDrawer"
    @update:mobileChannelDrawerSelect="handleMobileChannelDrawerSelect($event)"
    @update:show="showChannelDrawer = $event"
  />
  <div v-if="app.state.isMobile">
    <Embeds
      id="post-embeds"
      :editor="editor"
      :auto-focus="autoFocus"
      :selected-embed="selectedEmbed"
      :is-wide-screen="!app.state.isMobile"
      :is-comment-disabled="isCommentDisabled"
      editor-type="PostEditor"
      @update:selectedEmbed="changeEmbed"
      @update:isCommentDisabled="isCommentDisabled = $event"
    />
  </div>
</template>

<style scoped>
#post-publish__btn {
  width: 48%;
}

#post-cancel__btn {
  width: 48%;
}

#post-editor {
  overflow-y: auto;
  font-size: 16px;
}

#post-attachments {
  min-height: 210px;
  max-height: 300px;
  overflow: auto;
}

input {
  border: var(--border-style) var(--border);
  width: -webkit-fill-available;
  min-width: 91%;
}

input:focus {
  outline: 0;
}

.post-attachments::-webkit-scrollbar {
  display: none;
}

.post-editor-content-scroll {
  max-height: calc(100vh - 480px); /* Adjust the value as needed */
  overflow-y: auto;
}
@media (max-width: 930px) {
  input::placeholder {
    font-size: 20px !important;
    color: var(--fontSecondary) !important;
  }
  :deep(.hl-card) {
    border-bottom: var(--border-style) var(--border) !important;
    border-top: none !important;
    border-left: none !important;
    border-right: none !important;
    box-shadow: none !important;
  }
  .hl-card:deep(.hl-card-content) {
    padding-top: 5px !important;
    padding-bottom: 5px !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
  }
}
</style>
<style>
.post-editor-modal {
  max-width: 760px !important;
  width: 80% !important;
  position: fixed !important;
  top: 140px !important;
  left: 0px !important;
  right: 0px !important;
}

@media (max-width: 930px) {
  .post-editor-modal {
    position: fixed !important;
    width: 100% !important;
    min-width: 100% !important;
    top: 0px !important;
    left: 0px !important;
    right: 0px !important;
    overflow-y: scroll !important;
    padding: 12px !important;
  }

  .post-editor-modal .n-card__content {
    padding-bottom: 60px;
  }

  .ProseMirror p.is-editor-empty:first-child::before {
    color: var(--fontSecondary);
    font-weight: 400;
    font-size: 18px;
    font-family: var(--font-family) !important;
  }

  #post-editor-mobile-input .editor-container {
    padding-left: 0px !important;
    min-height: fit-content !important;
    max-height: fit-content !important;
  }

  .post-editor-modal .communities-giphy-image-post {
    width: 64px !important;
    height: 64px !important;
    object-fit: cover !important;
  }
}

body.user-is-tabbing #post-editor-description-area :focus-visible {
  outline: none !important;
  box-shadow: none !important;
}
</style>
